import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import CommonModal from "Components/CommonModal";
import SegmentControl from "Components/Sales/SegmentControl";
import SalesCustomerPaymentsModal from "Components/Sales/SalesCustomerPaymentsModal";
import { formatWon, formatKoreanUnit } from "Components/Sales/salesFormat";
import useSalesFilter from "utils/useSalesFilter";
import unwrapResponse from "utils/unwrapResponse";
import { catchError } from "JS/common";
import { getSalesCustomers } from "JS/API";

const PERIOD_OPTIONS = [
  { key: "month", label: "월별" },
  { key: "all", label: "전체" },
  { key: "custom", label: "기간 설정" },
];
const SORT_OPTIONS = [
  { key: "gross", label: "매출 총액 많은 순" },
  { key: "net", label: "순매출 많은 순" },
  { key: "count", label: "결제 건수 많은 순" },
  { key: "last_paid_at", label: "최근 결제일 순" },
];
const LIMIT = 20;

const styles = {
  toolbar: {
    display: "flex", gap: "14px", alignItems: "center", flexWrap: "wrap", backgroundColor: "#ffffff",
    border: "1px solid #e5e7eb", borderRadius: "12px", padding: "14px 18px", marginBottom: "20px",
  },
  monthNav: { display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", fontSize: "15px", color: "#1f2937" },
  navButton: {
    width: "28px", height: "28px", backgroundColor: "#f3f4f6", border: "none", borderRadius: "6px",
    cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: "#6b7280",
  },
  dateInput: { padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "13px", fontFamily: "inherit" },
  label: { fontSize: "13px", color: "#6b7280" },
  select: { padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "13px", fontFamily: "inherit", backgroundColor: "#ffffff", color: "#1f2937" },
  grow: { flex: 1 },
  search: { padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: "8px", fontSize: "13px", fontFamily: "inherit", width: "220px" },
  cards: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: "14px", marginBottom: "20px" },
  card: { backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "12px", padding: "18px 20px" },
  cardLabel: { fontSize: "13px", color: "#6b7280", marginBottom: "6px" },
  cardValue: { fontSize: "24px", fontWeight: "700", color: "#1f2937", lineHeight: 1.15, fontVariantNumeric: "tabular-nums" },
  cardSub: { fontSize: "12px", color: "#6b7280", marginTop: "6px" },
  tableCard: { backgroundColor: "#ffffff", border: "1px solid #e5e7eb", borderRadius: "12px", overflow: "hidden" },
  wrap: { overflowX: "auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: "13px", fontVariantNumeric: "tabular-nums" },
  th: { textAlign: "left", padding: "10px 12px", fontSize: "12px", color: "#6b7280", fontWeight: "600", backgroundColor: "#f8fafc", borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap" },
  td: { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", color: "#1f2937", verticalAlign: "middle" },
  right: { textAlign: "right" },
  row: { cursor: "pointer" },
  rank: { color: "#9ca3af", fontWeight: "600" },
  name: { fontWeight: "600" },
  sub: { display: "block", fontWeight: "400", color: "#9ca3af", fontSize: "11px" },
  manualBadge: { display: "inline-block", padding: "2px 8px", borderRadius: "6px", fontSize: "11px", fontWeight: "600", background: "#fef3c7", color: "#b45309" },
  muted: { color: "#9ca3af" },
  green: { color: "#059669" },
  tag: { display: "inline-flex", alignItems: "center", gap: "6px", fontSize: "12px", fontWeight: "600" },
  dot: { width: "8px", height: "8px", borderRadius: "2px", display: "inline-block" },
  pagination: { display: "flex", justifyContent: "center", alignItems: "center", gap: "12px", padding: "12px", borderTop: "1px solid #e5e7eb", fontSize: "13px", color: "#64748b" },
  pageBtn: { padding: "6px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", background: "#fff", cursor: "pointer", fontSize: "12px", display: "flex", alignItems: "center", gap: "4px" },
  loading: { display: "flex", justifyContent: "center", alignItems: "center", padding: "80px", color: "#9ca3af", fontSize: "14px" },
  empty: { padding: "60px", textAlign: "center", color: "#9ca3af", fontSize: "13px" },
};

const SalesCustomersTab = ({ service }) => {
  const navigate = useNavigate();
  const { year, month, period, startDate, endDate, sort, order, keyword, page, updateParams } = useSalesFilter();

  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [keywordInput, setKeywordInput] = useState(keyword);
  const [selected, setSelected] = useState(null);

  const [alert, setAlert] = useState("");
  const [alertBox, setAlertBox] = useState({ mode: "", context: "", bool: false });

  useEffect(() => {
    setKeywordInput(keyword);
  }, [keyword]);

  const filter = useMemo(() => {
    if (period === "month") return { period, year, month };
    if (period === "custom") return startDate && endDate ? { period, startDate, endDate } : null;
    return { period: "all" };
  }, [period, year, month, startDate, endDate]);

  const loadData = useCallback(async () => {
    if (!filter) return;
    setIsLoading(true);
    try {
      const result = await getSalesCustomers(filter, { service, keyword, sort, order, page, limit: LIMIT });
      if (typeof result !== "object" || !result?.data) {
        return catchError(result, navigate, setAlertBox, setAlert);
      }
      setData(unwrapResponse(result));
    } catch (error) {
      catchError(error, navigate, setAlertBox, setAlert);
    } finally {
      setIsLoading(false);
    }
  }, [filter, service, keyword, sort, order, page, navigate]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const changePeriod = useCallback((mode) => {
    if (mode === "month") updateParams({ period: mode, start: null, end: null, page: null });
    else if (mode === "all") updateParams({ period: mode, start: null, end: null, page: null });
    else updateParams({ period: mode, page: null });
  }, [updateParams]);

  const handlePrevMonth = () => {
    if (month === 1) updateParams({ year: year - 1, month: 12, page: null });
    else updateParams({ month: month - 1, page: null });
  };
  const handleNextMonth = () => {
    if (month === 12) updateParams({ year: year + 1, month: 1, page: null });
    else updateParams({ month: month + 1, page: null });
  };

  const submitKeyword = () => updateParams({ keyword: keywordInput.trim() || null, page: null });

  const handleAlertOk = useCallback(() => {
    if (alert === "logout") navigate("/");
  }, [alert, navigate]);

  const summary = data?.summary;
  const totalPages = data?.pagination?.total_pages || 1;
  const offset = (page - 1) * LIMIT;
  const top10Share = summary?.total_gross ? ((summary.top10_gross / summary.total_gross) * 100).toFixed(1) : "0.0";
  const repeatShare = summary?.customer_count ? ((summary.repeat_customer_count / summary.customer_count) * 100).toFixed(1) : "0.0";
  const avgGross = summary?.customer_count ? Math.round(summary.total_gross / summary.customer_count) : 0;

  return (
    <div>
      <div style={styles.toolbar}>
        <SegmentControl options={PERIOD_OPTIONS} value={period} onChange={changePeriod} compact />
        {period === "month" && (
          <div style={styles.monthNav}>
            <button type="button" aria-label="이전 달" style={styles.navButton} onClick={handlePrevMonth}><FaChevronLeft size={12} /></button>
            <span>{year}년 {month}월</span>
            <button type="button" aria-label="다음 달" style={styles.navButton} onClick={handleNextMonth}><FaChevronRight size={12} /></button>
          </div>
        )}
        {period === "custom" && (
          <div style={styles.monthNav}>
            <input type="date" aria-label="시작일" style={styles.dateInput} value={startDate} max={endDate || undefined}
              onChange={(e) => updateParams({ start: e.target.value, page: null })} />
            <span style={styles.muted}>~</span>
            <input type="date" aria-label="종료일" style={styles.dateInput} value={endDate} min={startDate || undefined}
              onChange={(e) => updateParams({ end: e.target.value, page: null })} />
          </div>
        )}
        <span style={styles.label}>정렬</span>
        <select aria-label="정렬" style={styles.select} value={sort} onChange={(e) => updateParams({ sort: e.target.value, page: null })}>
          {SORT_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
        </select>
        <span style={styles.grow} />
        <input
          type="search"
          aria-label="고객 검색"
          style={styles.search}
          placeholder="고객명 · 아이디 · 연락처 검색"
          value={keywordInput}
          onChange={(e) => setKeywordInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submitKeyword(); }}
        />
      </div>

      {period === "custom" && !filter ? (
        <div style={styles.loading}>시작일과 종료일을 선택해 주세요.</div>
      ) : isLoading || !data ? (
        <div style={styles.loading}>로딩 중...</div>
      ) : (
        <>
          <div style={styles.cards}>
            <div style={styles.card}>
              <div style={styles.cardLabel}>결제 고객 수</div>
              <div style={styles.cardValue}>{summary.customer_count.toLocaleString("ko-KR")} 명</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>상위 10명 매출 비중</div>
              <div style={styles.cardValue}>{top10Share}%</div>
              <div style={styles.cardSub}>{formatKoreanUnit(summary.top10_gross)} 원 / {formatKoreanUnit(summary.total_gross)} 원</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>재구매 고객</div>
              <div style={styles.cardValue}>{summary.repeat_customer_count.toLocaleString("ko-KR")} 명</div>
              <div style={styles.cardSub}>결제 2건 이상 · {repeatShare}%</div>
            </div>
            <div style={styles.card}>
              <div style={styles.cardLabel}>고객당 평균 매출</div>
              <div style={styles.cardValue}>{formatWon(avgGross)}</div>
            </div>
          </div>

          <div style={styles.tableCard}>
            <div style={styles.wrap}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>순위</th>
                    <th style={styles.th}>고객</th>
                    <th style={styles.th}>구분</th>
                    <th style={{ ...styles.th, ...styles.right }}>결제 건수</th>
                    <th style={{ ...styles.th, ...styles.right }}>매출 총액</th>
                    <th style={{ ...styles.th, ...styles.right }}>순매출</th>
                    <th style={styles.th}>최근 결제일</th>
                    <th style={styles.th}>첫 결제일</th>
                    <th style={styles.th}>주요 태그</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? (
                    <tr><td colSpan={9} style={styles.empty}>조건에 맞는 고객이 없습니다.</td></tr>
                  ) : data.items.map((c, i) => (
                    <tr key={c.customer_key} style={styles.row} onClick={() => setSelected(c)}>
                      <td style={{ ...styles.td, ...styles.rank }}>{offset + i + 1}</td>
                      <td style={{ ...styles.td, ...styles.name }}>
                        {c.user_name || "-"}
                        <span style={styles.sub}>{c.is_manual ? (c.contact || "연락처 없음") : c.user_id}</span>
                      </td>
                      <td style={styles.td}>{c.is_manual ? <span style={styles.manualBadge}>수동</span> : <span style={styles.muted}>회원</span>}</td>
                      <td style={{ ...styles.td, ...styles.right }}>{c.count}건</td>
                      <td style={{ ...styles.td, ...styles.right, fontWeight: "600" }}>{formatWon(c.gross)}</td>
                      <td style={{ ...styles.td, ...styles.right, ...styles.green }}>{formatWon(c.net)}</td>
                      <td style={styles.td}>{c.last_paid_at}</td>
                      <td style={{ ...styles.td, ...styles.muted }}>{c.first_paid_at}</td>
                      <td style={styles.td}>
                        {c.top_tag ? (
                          <span style={styles.tag}><span style={{ ...styles.dot, backgroundColor: c.top_tag.color }} />{c.top_tag.name}</span>
                        ) : <span style={styles.muted}>-</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div style={styles.pagination}>
                <button type="button" style={styles.pageBtn} disabled={page === 1} onClick={() => updateParams({ page: page - 1 })}>
                  <FaChevronLeft size={10} /> 이전
                </button>
                <span>{page} / {totalPages} 페이지</span>
                <button type="button" style={styles.pageBtn} disabled={page === totalPages} onClick={() => updateParams({ page: page + 1 })}>
                  다음 <FaChevronRight size={10} />
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {selected && (
        <SalesCustomerPaymentsModal
          customerKey={selected.customer_key}
          customerName={selected.user_name}
          filter={filter}
          service={service}
          onClose={() => setSelected(null)}
        />
      )}

      {alertBox.bool && (
        <CommonModal setModal={setAlertBox} modal={alertBox} okFn={handleAlertOk} />
      )}
    </div>
  );
};

export default SalesCustomersTab;
